import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

const CURRENT_APP_VERSION =
  typeof import.meta.env.VITE_SCORENEST_APP_VERSION === 'string' &&
  import.meta.env.VITE_SCORENEST_APP_VERSION.trim()
    ? import.meta.env.VITE_SCORENEST_APP_VERSION.trim()
    : 'dev';
const VERSION_MANIFEST_URL = '/version.json';
const DEFAULT_CHECK_INTERVAL_MS = 60_000;
const REPORT_CHECK_INTERVAL_MS = 20_000;
const RELOAD_RETRY_AFTER_MS = 45_000;
const RELOAD_STORAGE_PREFIX = 'scorenest:auto-reload-version:';
const VERSION_WATCHER_ENABLED =
  import.meta.env.PROD || import.meta.env.VITE_ENABLE_VERSION_WATCHER === 'true';

type VersionManifest = {
  version?: unknown;
};

function isSafeAutoReloadPath(pathname: string): boolean {
  const normalizedPath = pathname.toLowerCase();
  if (
    normalizedPath === '/' ||
    normalizedPath.startsWith('/admin') ||
    normalizedPath.startsWith('/reports/login') ||
    normalizedPath.startsWith('/reports/access') ||
    normalizedPath.startsWith('/shared/')
  ) {
    return false;
  }

  return (
    normalizedPath.startsWith('/report/') ||
    normalizedPath === '/reports' ||
    normalizedPath === '/reports/recent' ||
    normalizedPath.startsWith('/compare') ||
    normalizedPath.startsWith('/methodology') ||
    normalizedPath.endsWith('-policy') ||
    normalizedPath === '/terms-of-service' ||
    normalizedPath === '/account' ||
    normalizedPath === '/pricing'
  );
}

function isReportReadingPath(pathname: string): boolean {
  const normalizedPath = pathname.toLowerCase();
  return (
    normalizedPath.startsWith('/report/') ||
    normalizedPath === '/reports' ||
    normalizedPath === '/reports/recent' ||
    normalizedPath.startsWith('/compare')
  );
}

async function fetchLatestVersion(): Promise<string | null> {
  const response = await fetch(`${VERSION_MANIFEST_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });

  if (!response.ok) return null;

  const manifest = (await response.json()) as VersionManifest;
  return typeof manifest.version === 'string' && manifest.version.trim()
    ? manifest.version.trim()
    : null;
}

function canAutoReloadForVersion(version: string): boolean {
  try {
    const key = `${RELOAD_STORAGE_PREFIX}${version}`;
    const lastAttemptAt = Number(sessionStorage.getItem(key) ?? 0);
    if (Number.isFinite(lastAttemptAt) && Date.now() - lastAttemptAt < RELOAD_RETRY_AFTER_MS) {
      return false;
    }
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

function clearReloadAttemptThrottle(version: string) {
  try {
    sessionStorage.removeItem(`${RELOAD_STORAGE_PREFIX}${version}`);
  } catch {
    // Best-effort only.
  }
}

export function FrontendVersionWatcher() {
  const location = useLocation();
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const checkingRef = useRef(false);
  const pathname = location.pathname;
  const safeAutoReload = useMemo(() => isSafeAutoReloadPath(pathname), [pathname]);
  const reportReadingPath = useMemo(() => isReportReadingPath(pathname), [pathname]);

  const reloadForVersion = useCallback((version: string, options: { force?: boolean } = {}) => {
    if (!VERSION_WATCHER_ENABLED) return;
    if (!options.force && !canAutoReloadForVersion(version)) {
      setAvailableVersion(version);
      return;
    }
    if (options.force) {
      clearReloadAttemptThrottle(version);
    }
    window.location.reload();
  }, []);

  const checkVersion = useCallback(async () => {
    if (!VERSION_WATCHER_ENABLED) return;
    if (checkingRef.current) return;
    checkingRef.current = true;

    try {
      const latestVersion = await fetchLatestVersion();
      if (!latestVersion || latestVersion === CURRENT_APP_VERSION) return;

      if (safeAutoReload) {
        reloadForVersion(latestVersion);
        return;
      }

      setAvailableVersion(latestVersion);
    } catch {
      // Best-effort only. A failed version probe should never interrupt report reading.
    } finally {
      checkingRef.current = false;
    }
  }, [reloadForVersion, safeAutoReload]);

  useEffect(() => {
    if (!VERSION_WATCHER_ENABLED) return;
    void checkVersion();
  }, [checkVersion]);

  useEffect(() => {
    if (!VERSION_WATCHER_ENABLED) return;
    const intervalMs = reportReadingPath ? REPORT_CHECK_INTERVAL_MS : DEFAULT_CHECK_INTERVAL_MS;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void checkVersion();
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [checkVersion, reportReadingPath]);

  useEffect(() => {
    if (!VERSION_WATCHER_ENABLED) return;
    const checkWhenVisible = () => {
      if (document.visibilityState === 'hidden') return;
      void checkVersion();
    };

    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkWhenVisible);
    window.addEventListener('scorenest:check-version', checkWhenVisible);

    return () => {
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkWhenVisible);
      window.removeEventListener('scorenest:check-version', checkWhenVisible);
    };
  }, [checkVersion]);

  useEffect(() => {
    if (!VERSION_WATCHER_ENABLED) return;
    if (!availableVersion || !safeAutoReload) return;
    reloadForVersion(availableVersion);
  }, [availableVersion, reloadForVersion, safeAutoReload]);

  if (!VERSION_WATCHER_ENABLED || !availableVersion) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-w-md rounded-[18px] border border-[#e6d1b8] bg-[#fffaf3] px-4 py-3 text-[#3b2d24] shadow-[0_18px_42px_rgba(77,56,36,0.18)]">
      <div className="text-sm font-black">A fresh ScoreNest update is ready.</div>
      <div className="mt-1 text-xs font-semibold text-[#6d6258]">
        Finish what you are typing, then update the app.
      </div>
      <button
        type="button"
        onClick={() => reloadForVersion(availableVersion, { force: true })}
        className="mt-3 rounded-full bg-[#5f3b22] px-4 py-2 text-sm font-black text-white shadow-[0_10px_18px_rgba(95,59,34,0.2)] transition hover:bg-[#4d2f1b]"
      >
        Update now
      </button>
    </div>
  );
}
